import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useAppForm } from '@/lib/form'
import {
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
} from '@/lib/services/customers/customers.query'
import { useUpdateCartMutation, useIsCartMutating } from '@/lib/services/carts/carts.query'
import { customerSchema } from '@/lib/services/customers/customers.schema'
import { CustomerSourceType } from '@/lib/enums/CustomerSourceType'
import { getErrorMessage } from '@/lib/utils'
import { toast } from 'sonner'
import {
  cleanCustomerAddressDraft,
  customerAddressDraftFrom,
} from '@/lib/services/customers/customers.address'
import { CustomerAddressFieldInputs } from '@/components/forms/CustomerAddressFields'
import { MapPin, Trash2 } from 'lucide-react'
import { useState } from 'react'

export function CustomerModal({
  open,
  onOpenChange,
  cart,
  createCartWithDefaults,
  initialSearchQuery = '',
}) {
  const createCustomerMutation = useCreateCustomerMutation()
  const updateCustomerMutation = useUpdateCustomerMutation()
  const updateCartMutation = useUpdateCartMutation()
  const isCartMutating = useIsCartMutating()
  const initialData = cart?.purchaser
  const isEditing = !!initialData?.id
  const [isAddressVisible, setIsAddressVisible] = useState(Boolean(initialData?.physical_address))

  const isMutationPending =
    createCustomerMutation.isPending || updateCustomerMutation.isPending || isCartMutating > 0

  // Determine if search query is numeric (phone) or not (email)
  const initialValues = (() => {
    if (initialData) {
      return {
        name: initialData.name || '',
        phone: initialData.phone || '',
        email: initialData.email || '',
        physical_address: customerAddressDraftFrom(initialData.physical_address),
      }
    }

    const trimmedQuery = initialSearchQuery?.trim() || ''
    const isNumeric = /^\d+$/.test(trimmedQuery.replace(/\D/g, ''))

    return {
      name: '',
      phone: isNumeric ? trimmedQuery : '',
      email: !isNumeric && trimmedQuery ? trimmedQuery : '',
      physical_address: customerAddressDraftFrom(null),
    }
  })()

  const form = useAppForm({
    defaultValues: initialValues,
    validators: {
      onSubmit: customerSchema,
      onSubmitAsync: async ({ value, formApi }) => {
        try {
          const customerPayload = {
            ...value,
            physical_address: cleanCustomerAddressDraft(value.physical_address),
          }
          let customer
          if (isEditing) {
            customer = await updateCustomerMutation.mutateAsync({
              customerId: initialData.id,
              ...customerPayload,
            })
            toast.success('Customer updated successfully')
          } else {
            customer = await createCustomerMutation.mutateAsync({
              ...customerPayload,
              source_type: CustomerSourceType.POS.Value,
            })
            toast.success('Customer created successfully')
          }

          // Handle customer selection - update cart with customer
          if (customer) {
            let currentCartId = cart?.id
            if (!currentCartId) {
              const newCart = await createCartWithDefaults()
              currentCartId = newCart.id
            }
            if (currentCartId) {
              await updateCartMutation.mutateAsync({
                cartId: currentCartId,
                data: { purchaser_customer_id: customer.id },
              })
            }
          }

          onOpenChange(false)
          formApi.reset()
        } catch (error) {
          toast.error(`Failed to ${isEditing ? 'update' : 'create'} customer`, {
            description: getErrorMessage(error),
          })
          if (error?.response?.data?.errors) {
            return { fields: error.response.data.errors }
          }
        }
      },
    },
  })

  const handleClose = () => {
    onOpenChange(false)
    form.reset()
  }

  const handleRemoveAddress = () => {
    form.setFieldValue('physical_address', customerAddressDraftFrom(null))
    setIsAddressVisible(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent viewport="contained" className="flex flex-col">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Update Customer' : 'Add New Customer'}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update customer information'
              : 'Enter customer information to create a new customer'}
          </DialogDescription>
        </DialogHeader>

        <form.AppForm key={`customer-form-${cart?.purchaser?.id || initialSearchQuery}`}>
          <div className="flex min-h-0 flex-1 flex-col">
            <div
              data-slot="customer-modal-body"
              className="-ml-1 min-h-0 flex-1 overflow-y-auto px-1"
            >
              <form.Form>
                <form.FormError />
                <div className="mt-4 space-y-4">
                  <form.AppField name="name">
                    {(field) => (
                      <field.TextInput
                        id="customer-name"
                        label="Name"
                        placeholder="Customer name"
                      />
                    )}
                  </form.AppField>

                  <form.AppField name="phone">
                    {(field) => <field.PhoneInput label="Phone Number" />}
                  </form.AppField>

                  <form.AppField name="email">
                    {(field) => <field.EmailInput label="Email" />}
                  </form.AppField>

                  {isAddressVisible ? (
                    <div className="space-y-4 rounded-lg border bg-muted/20 p-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-sm font-medium">
                          <MapPin className="size-4 text-muted-foreground" />
                          Customer Address
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handleRemoveAddress}
                        >
                          <Trash2 className="size-4" />
                          Remove
                        </Button>
                      </div>
                      <CustomerAddressFieldInputs
                        form={form}
                        id="pos-customer"
                        prefix="physical_address"
                      />
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={() => setIsAddressVisible(true)}
                    >
                      <MapPin className="size-4" />
                      Add customer address (optional)
                    </Button>
                  )}
                </div>
              </form.Form>
            </div>
            <DialogFooter className="shrink-0 pt-4">
              <Button
                variant="outline"
                type="button"
                onClick={handleClose}
                disabled={isMutationPending}
              >
                Cancel
              </Button>
              <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                {([canSubmit, isSubmitting]) => (
                  <Button
                    type="button"
                    onClick={() => form.handleSubmit()}
                    disabled={!canSubmit || isMutationPending}
                  >
                    {isSubmitting
                      ? isEditing
                        ? 'Updating...'
                        : 'Creating...'
                      : isEditing
                        ? 'Update Customer'
                        : 'Create Customer'}
                  </Button>
                )}
              </form.Subscribe>
            </DialogFooter>
          </div>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  )
}
